/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, lazy, Suspense } from "react";
import axiosInstance from "../../api/axiosInstance";
import { Tab } from "@headlessui/react";
import { Loader2 } from "lucide-react";
import clsx from "clsx";

import SharedSettings   from "./shared/SharedSettings";
import StickySaveBar    from "./shared/StickySaveBar";
import ModeSelector     from "./shared/ModeSelector";
import { validateAll } from "./shared/validation";
import { uploadAllImages } from './utils/upload_pics';
import { useSearchParams } from "react-router-dom";
import { CurrencyContext } from "./shared/context/CurrencyContext";
import FeePoliciesForm  from "./shared/FeePoliciesForm";
import OperationalHoursForm from "./shared/OperationalHoursForm";
import Required from "./utils/Required";
import { hasErrPref } from './shared/validation';
import { sanitizeReservationPayload } from "./utils/sanitizeReservationPayload";
// ------------- Lazy-loaded mode forms ------------------------------------
const PersonalServicesForm = lazy(() => import("./modes/PersonalServicesForm"));
const TableInventoryForm   = lazy(() => import("./modes/TableInventoryForm"));
const ClassBasedForm       = lazy(() => import("./modes/ClassBasedForm"));
const AccommodationForm    = lazy(() => import("./modes/Accomodation/AccomodationForm"));
const BundlesForm  = lazy(() => import("./shared/BundlesForm"));
const TaxFeesForm  = lazy(() => import("./shared/TaxFeesForm"));
const AgeRestrictionsForm = lazy(() => import("./shared/AgeRestrictionsForm"));
const NotificationsForm = lazy(() => import("./shared/NotificationsForm"));
const EditRulesForm = lazy(() => import("./shared/EditRulesForm"));

export const MODE_LABELS = {
  personal_services: "Personal Services",
  table_inventory  : "Table Inventory",
  class_based      : "Class-Based",
  accommodation    : "Accommodation",
};

const MODE_COMPONENTS = {
  personal_services: PersonalServicesForm,
  table_inventory  : TableInventoryForm,
  class_based      : ClassBasedForm,
  accommodation    : AccommodationForm,
};

const TAB_ERROR_PREFIXES = {
  personal_services: ["personal_services", "svc", "equip"],
  table_inventory: ["table_inventory", "seating", "table"],
  class_based: ["class_based", "class"],
  accommodation: ["accommodation", "room"],
  bundles: ["bundles"],
  taxes_fees: ["taxes", "taxes_fees"],
  fee_policies: ["fee_policies"],
  notifications: ["notifications"],
  age_restrictions: ["age_restrictions"],
  operational_hours_rules: ["operational_hours_rules"],
  edit_rules: ["edit_rules"],
  shared: [
    "timezone",
    "base_currency",
    "booking_limits",
    "booking_cutoff_minutes",
    "booking_lead_days",
    "manual_approval_settings",
    "booking_rules",
  ],
};
  const TopTab = ({ children, hasError  }) => (
    <Tab
      className={({ selected }) =>
        clsx(
          /*  flex-none  → only as wide as its label  */
          "flex-none rounded-lg px-4 py-2.5 text-sm font-medium focus:outline-none",
          selected ? "bg-white shadow" : "text-gray-600 hover:bg-white/50",
          hasError && "text-red-600"
        )
      }
    >
     {children}{hasError && <Required />}
    </Tab>
  );


  const TabPanel = ({ children }) => (
    <Tab.Panel className="mt-6 space-y-6">{children}</Tab.Panel>
  );

  const Spinner = () => (
    <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
  );

const CopyConfigDropdown = lazy(() => import("./shared/CopyConfigDropdown"));
  const FIXED_TAB_COUNT = 8; // Bundles + Tax & Fees + Fee Policies + Notifications + Age + Operational Hours + Edit Rules + Shared Settings

const defaultTimezone =
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

// ---------------- Deep merge util (arrays overwrite) ---------------------
const deepMerge = (base, patch) => {
  // 1. nothing to merge → keep current value
  if (patch === undefined) return base;

 // 2. arrays → replace, *unless* patch is undefined (handled above)
  if (Array.isArray(base) && Array.isArray(patch)) {
    return patch;
  }
  // 3. objects → deep merge
  if (typeof base === "object" && typeof patch === "object") {
    const keys = new Set([...Object.keys(base || {}), ...Object.keys(patch || {})]);
    return [...keys].reduce((acc, k) => {
      acc[k] = deepMerge(base?.[k], patch?.[k]);
      return acc;
    }, {});
  }

  // 4. primitives → take patch
  return patch;
};
function stampMode(allModes, data) {
  allModes.forEach(m => {
    const list =
      m === "personal_services" ? data.personal_services?.services :
      m === "class_based"       ? data.class_based?.classes :
      m === "accommodation"     ? data.accommodation?.room_types :
      m === "table_inventory"   ? data.table_inventory?.services :
      [];
    (list ?? []).forEach(o => { if (!o.mode) o.mode = m; });
  });
}

// ------------------------------------------------------------------------
export default function ReservationConfigForm() {
  /* ⬇️  CHANGED: modes is now an array */
  const [modes,    setModes]   = useState([]);
  const [formData, setFormData] = useState({ timezone: defaultTimezone });

  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [errors,   setErrors]   = useState({});
  const [search]   = useSearchParams();
  const [tabIndex, setTabIndex] = useState(0);
  const [detailStep, setDetailStep] = useState(0);   // which mode-step is open
  const [mainTab, setMainTab] = useState(0);
  const [serviceOpen, setServiceOpen] = useState({});
  const [ready,    setReady]    = useState(false); 

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    html.style.overflow = "";
    html.style.overscrollBehaviorY = "";
    body.style.overflow = "";
    body.style.overscrollBehaviorY = "";
    body.style.position = "";
    body.style.top = "";
    body.style.width = "";
  }, []);

  // ---------------- Fetch config ----------------------------------------
useEffect(() => {
  const businessId = search.get("business_id");
  if (!businessId) return;

  let mounted = true;
  axiosInstance
    .get("/reservation/config", { params: { business_id: businessId } })
    .then(({ data }) => {
      if (!mounted) return;

      console.log("✅ /reservation/config response:", data);

      /* ✅  Back-compat: data.mode (old) ➜ wrap to array */
      const loadedModes = data.modes ?? (data.mode ? [data.mode] : []);
      setModes(loadedModes);
      setFormData((prev) => deepMerge(prev, data));
      stampMode(loadedModes, data); 
    })
    .finally(() => mounted && setLoading(false));

  return () => (mounted = false);
}, []);

  useEffect(() => {
    if (detailStep >= modes.length) setDetailStep(0);
  }, [modes, detailStep]);

  useEffect(() => {
    const max = modes.length + FIXED_TAB_COUNT;
    if (mainTab > max) setMainTab(0);
  }, [modes, mainTab]);

  useEffect(() => {
   const v = validateAll(modes, formData);
   setErrors(v);
   console.log(errors);
   setReady(Object.keys(v).length === 0);
  }, [formData, modes]);

 const updateFormData = (patch) => {
   if (typeof patch !== "object") return;
   setFormData(prev => deepMerge(prev, patch));
         console.log(formData);

 };

  // ---------------- Save / Preview --------------------------------------
  const handleSave = async (preview = false, silent = false) => {
    const errs = validateAll(modes, formData);
    if (Object.keys(errs).length) {
      // show & scroll
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector(`[data-errkey="${firstKey}"]`);
      el?.scrollIntoView({ behavior:'smooth', block:'center' });
      return;
    }
    setSaving(true);
    try {
      console.log(formData);
      const businessId = search.get("business_id");
      await uploadAllImages(formData, businessId, modes);
      const cleaned = sanitizeReservationPayload(formData);
      const endpoint = preview
        ? "/reservation/preview"
        : "/reservation/config";

      const res = await axiosInstance.post(
        endpoint,
        cleaned,
        { params: { business_id: search.get("business_id") } }
      );

      if (!silent) {
        if (res?.data?.success === false) {
          window.toast.error(res.data.message || "Save failed");
        } else {
          window.toast.success(preview ? "Preview ready" : "Saved");
        }
      }
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Error saving – try again";
      window.toast.error(msg);
    }
    finally {
      setSaving(false);
    }
  };

  const handlePreviewLocal = () => {
    console.log("hey");
    if (!ready) return;
    setPreviewOpen(true);
    console.log("hello");
  };

 const hasAny = (prefixes) => prefixes.some(p => hasErrPref(errors, p));
  // ---------------- Render ----------------------------------------------
  if (loading) {
    return (
      <div className="flex h-60 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  /* 🔄  Build the array of mode-specific forms */
  const modeForms = modes.map((m) => {
    const Comp = MODE_COMPONENTS[m];
    const employees = formData.employees ?? [];
    if (!Comp) return null;
    return (
      <div key={m} className="mb-10">
        <h4 className="mb-2 text-md font-semibold">{MODE_LABELS[m]}</h4>
        <Suspense
          fallback={
            <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
          }
        >
          <Comp
            value={formData[m]}
            onChange={(v) => updateFormData({ [m]: v })}
            employees={employees}
          />
        </Suspense>
      </div>
    );
  });

return (
  <CurrencyContext.Provider value={formData.base_currency || "EUR"}>
    <div className="tailwind h-screen overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: "touch" }}>
      <div className="relative mx-auto max-w-6xl px-4 py-8 pb-32 lg:flex lg:space-x-8">
        {/* … CopyConfigDropdown stays as-is … */}

        <div className="flex-1 space-y-6">
{/* ─────────────────── TOP-LEVEL TABS ─────────────────── */}
  <Tab.Group selectedIndex={mainTab} onChange={setMainTab} as="div">
    {/* ---------- TAB LIST  (titles only!) ------------------------------ */}
    <Tab.List className="flex space-x-1 overflow-x-auto whitespace-nowrap rounded-xl bg-gray-100 p-1">
      <TopTab>Select Mode(s)</TopTab>

      {modes.map((m) => (
        <TopTab key={m} hasError={hasAny(TAB_ERROR_PREFIXES[m] || [m])}>
          {MODE_LABELS[m]}
          </TopTab>
      ))}

      <TopTab hasError={hasAny(TAB_ERROR_PREFIXES.bundles)}>Bundles</TopTab>
      <TopTab hasError={hasAny(TAB_ERROR_PREFIXES.taxes_fees)}>Tax&nbsp;&amp;&nbsp;Fees</TopTab>
      <TopTab hasError={hasAny(TAB_ERROR_PREFIXES.fee_policies)}>Fee&nbsp;Policies</TopTab>
      <TopTab hasError={hasAny(TAB_ERROR_PREFIXES.notifications)}>Notification&nbsp;Config</TopTab>
      <TopTab hasError={hasAny(TAB_ERROR_PREFIXES.age_restrictions)}>Age&nbsp;Restrictions</TopTab>
  <TopTab hasError={hasAny(TAB_ERROR_PREFIXES.operational_hours_rules)}>Operational&nbsp;Hours</TopTab>
  <TopTab hasError={hasAny(TAB_ERROR_PREFIXES.edit_rules)}>Edit&nbsp;Rules</TopTab>
      <TopTab hasError={hasAny(TAB_ERROR_PREFIXES.shared)}>Shared Settings</TopTab>
    </Tab.List>

    {/* ---------- TAB PANELS  (actual content) -------------------------- */}
    {/* 0 → Mode selector */}
    <TabPanel>
      <ModeSelector
        value={modes}
        onChange={(newModes) => {
          setModes(newModes);
          updateFormData({ modes: newModes });
        }}
      />
    </TabPanel>

    {/* 1…n → one panel per active mode */}
  {modes.map((m, cfgIdx) => {
  const ModeForm = MODE_COMPONENTS[m];
  const employees = formData.employees ?? [];
  return (
    <TabPanel key={`tab-panel-${m}`}>
      <Suspense fallback={<Spinner />}>
        <ModeForm              
          value={formData[m]}
          onChange={v => updateFormData({ [m]: v })}
          employees={employees}
          openMap={serviceOpen}
          setOpenMap={setServiceOpen}
          configIndex={cfgIdx}
          errors={errors} 
        />
      </Suspense>
    </TabPanel>
  );
})}

    {/* Bundles */}
    <TabPanel>
      <Suspense fallback={<Spinner />}>
        <BundlesForm
          value={formData.bundles}
          modes={modes}
          modeData={formData}
          errors={errors}
          onChange={(v) => updateFormData({ bundles: v })}
        />
      </Suspense>
    </TabPanel>

    {/* Tax & Fees */}
    <TabPanel>
      <Suspense fallback={<Spinner />}>
        <TaxFeesForm
          value={formData.taxes_fees}
          modeData={formData}
          errors={errors}
          onChange={(v) => updateFormData({ taxes_fees: v })}
        />
      </Suspense>
    </TabPanel>
    <TabPanel>
      <Suspense fallback={<Spinner />}>
        <FeePoliciesForm
          value={formData.fee_policies}
          modeData={formData}
          errors={errors}
          onChange={v => updateFormData({ fee_policies: v })}
        />
      </Suspense>
    </TabPanel>
    <TabPanel>
      <Suspense fallback={<Spinner />}>
        <NotificationsForm
          value={formData.notifications || []}
          modeData={formData}
          errors={errors}
          onChange={(v) => updateFormData({ notifications: v })}
        />
      </Suspense>
    </TabPanel>

    <TabPanel>
      <Suspense fallback={<Spinner />}>
        <AgeRestrictionsForm
          value={formData.age_restrictions}
          modeData={formData}
          errors={errors}
          onChange={v => updateFormData({ age_restrictions: v })}
        />
      </Suspense>
    </TabPanel>

    {/* Operational Hours */}
    <TabPanel>
      <Suspense fallback={<Spinner />}>
        <OperationalHoursForm
          value={formData.operational_hours_rules}
          modeData={formData}
          errors={errors}
          onChange={v => updateFormData({ operational_hours_rules: v })}
        />
      </Suspense>
    </TabPanel>

    {/* Edit Rules */}
    <TabPanel>
      <Suspense fallback={<Spinner />}>
        <EditRulesForm
          value={formData.edit_rules || []}
          modeData={formData}
          errors={errors}
          onChange={v => updateFormData({ edit_rules: v })}
        />
      </Suspense>
    </TabPanel>

    {/* Shared Settings */}
    <TabPanel>
      <SharedSettings
        value={formData}
        onChange={updateFormData}
        modes={modes}
        errors={errors}
      />
    </TabPanel>
  </Tab.Group>


    </div>

      <StickySaveBar
        mobile
        mode={modes.length > 0}
        canSave={ready}
        errors={errors}
        saving={saving}
        onSave={() => handleSave(false)}
        onPreview={handlePreviewLocal}
      />
      {/* ─────────────── FULL-SCREEN PREVIEW ─────────────── */}
      {previewOpen && (
        <div className="fixed inset-0 z-40 bg-white overflow-y-auto">
          {/* Close button */}
          <button
            type="button"
            onClick={() => {
              document.body.style.overflow = "";
              setPreviewOpen(false)}}
            className="absolute top-4 left-4 flex items-center gap-1 text-gray-500 hover:text-black"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M16.24 7.76a.75.75 0 010 1.06L13.06 12l3.18 3.18a.75.75 0 11-1.06 1.06L12 13.06l-3.18 3.18a.75.75 0 11-1.06-1.06L10.94 12 7.76 8.82a.75.75 0 111.06-1.06L12 10.94l3.18-3.18a.75.75 0 011.06 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="sr-only">Close preview</span>
          </button>

        </div>
      )}
      </div>
    </div>
  </CurrencyContext.Provider>
);
}
